import notify from '../../utilities/notify'
import { MAX_ALLOWED_BYTES } from '../../utilities/fileUtil'
import {
  IMAGE_SIGNED_ID_ATTR,
  getImageSignedId,
  rememberImageSignedId,
  uploadImageToStorage
} from './uploadImage'

function readFileAsDataUrl (file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(new Error('Failed to read image'))
    reader.readAsDataURL(file)
  })
}

export function stampSignedIdAttributes (editor) {
  const body = editor?.getBody()
  if (!body) return

  body.querySelectorAll('img').forEach((img) => {
    if (img.getAttribute(IMAGE_SIGNED_ID_ATTR)) return
    const src = img.getAttribute('src')
    const signedId = src && getImageSignedId(src)
    if (signedId) {
      img.setAttribute(IMAGE_SIGNED_ID_ATTR, signedId)
    }
  })
}

export function uploadPendingDataImages (editor) {
  const body = editor?.getBody()
  if (!body) return

  body.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src') || ''
    if (!src.startsWith('data:image')) return
    if (img.getAttribute(IMAGE_SIGNED_ID_ATTR) || getImageSignedId(src)) {
      stampSignedIdAttributes(editor)
      return
    }
    if (img.dataset.uploadPending === 'true') return
    img.dataset.uploadPending = 'true'

    fetch(src)
      .then((response) => response.blob())
      .then((blob) => uploadImageToStorage(blob, { filename: 'pasted-image.png' }))
      .then((uploaded) => {
        rememberImageSignedId(src, uploaded.signed_id)
        img.setAttribute(IMAGE_SIGNED_ID_ATTR, uploaded.signed_id)
        img.removeAttribute('data-upload-pending')
      })
      .catch((error) => {
        img.removeAttribute('data-upload-pending')
        notify('error', error?.message || 'Failed to upload image')
      })
  })
}

function isEditorActive (editor) {
  return Boolean(editor) && !editor.removed
}

function pickImageFile () {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'

    let settled = false
    const finish = (file) => {
      if (settled) return
      settled = true
      resolve(file)
    }

    input.addEventListener('change', () => finish(input.files?.[0] || null))
    input.addEventListener('cancel', () => finish(null))
    input.click()
  })
}

async function uploadPickedImage (file) {
  if (!file) return null

  if (file.size > MAX_ALLOWED_BYTES) {
    notify('error', 'Image must be 10 MB or smaller')
    return null
  }

  try {
    const dataUrl = await readFileAsDataUrl(file)
    const uploaded = await uploadImageToStorage(file, { filename: file.name })
    rememberImageSignedId(dataUrl, uploaded.signed_id)
    return {
      dataUrl,
      filename: file.name,
      signedId: uploaded.signed_id
    }
  } catch (error) {
    notify('error', error?.message || 'Failed to upload image')
    return null
  }
}

export async function insertImageFromComputer (editor) {
  if (!isEditorActive(editor)) return

  const result = await uploadPickedImage(await pickImageFile())
  if (!result || !isEditorActive(editor)) return

  const attrs = {
    src: result.dataUrl,
    alt: result.filename,
    title: result.filename
  }
  if (result.signedId) {
    attrs[IMAGE_SIGNED_ID_ATTR] = result.signedId
  }

  editor.insertContent(editor.dom.createHTML('img', attrs))
}
